import { Badge } from "@/components/ui/badge";
import { listTenants, type TenantStatus } from "@/lib/db/queries/tenants";
import { logActivity } from "@/lib/db/audit";
import { getSession } from "@/lib/auth/session";

export const dynamic = "force-dynamic";

const STATUS_TONE: Record<TenantStatus, "warning" | "success" | "danger" | "neutral"> = {
  trial: "warning",
  active: "success",
  suspended: "danger",
  canceled: "neutral",
};

const STATUS_LABEL: Record<TenantStatus, string> = {
  trial: "Essai",
  active: "Actif",
  suspended: "Suspendu",
  canceled: "Résilié",
};

const PLAN_LABEL: Record<string, string> = {
  starter: "Starter",
  pro: "Pro",
  enterprise: "Enterprise",
};

const dateFmt = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const priceFmt = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

export default async function TenantsPage() {
  const session = await getSession();
  let rows: Awaited<ReturnType<typeof listTenants>> = [];
  let loadError: string | null = null;

  try {
    rows = await listTenants();
    void logActivity({
      type: "superadmin.tenants_list",
      title: "Lecture du listing tenants",
      description: `${rows.length} tenants lus`,
      user_id: session?.user.id,
    });
  } catch (err) {
    loadError = err instanceof Error ? err.message : "Erreur inconnue";
  }

  return (
    <div className="px-6 py-6 lg:px-10">
      <header className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Tenants</h1>
          <p className="text-sm text-muted-foreground">
            Organisations clientes du SaaS — un tenant correspond à un compte
            payant.
          </p>
        </div>
        {!loadError && (
          <span className="text-xs text-muted-foreground tabular-nums">
            {rows.length} tenant{rows.length > 1 ? "s" : ""}
          </span>
        )}
      </header>

      {loadError && (
        <div className="rounded-md border border-red-200 bg-danger-subtle px-4 py-3 text-sm text-danger">
          Impossible de charger les tenants : {loadError}
        </div>
      )}

      {!loadError && rows.length === 0 && (
        <div className="rounded-md border border-border bg-surface px-4 py-8 text-center text-sm text-muted-foreground">
          Aucun tenant en base. C&apos;est très calme.
        </div>
      )}

      {!loadError && rows.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border bg-surface">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5">Nom</th>
                <th className="px-4 py-2.5">Slug</th>
                <th className="px-4 py-2.5">Statut</th>
                <th className="px-4 py-2.5">Plan</th>
                <th className="px-4 py-2.5 text-right">Tech.</th>
                <th className="px-4 py-2.5 text-right">Sites</th>
                <th className="px-4 py-2.5 text-right">Machines</th>
                <th className="px-4 py-2.5">Créé le</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((t) => (
                <tr key={t.id} className="text-foreground hover:bg-surface-2/60">
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{t.name}</div>
                    {t.admin_email && (
                      <div className="text-xs text-muted-foreground">
                        {t.admin_email}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                    {t.slug}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge tone={STATUS_TONE[t.status]}>
                      {STATUS_LABEL[t.status]}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">
                      {t.plan_name ?? PLAN_LABEL[t.plan_code] ?? t.plan_code}
                    </div>
                    {t.plan_monthly_price && (
                      <div className="text-xs text-muted-foreground tabular-nums">
                        {priceFmt.format(Number(t.plan_monthly_price))}/mois
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {t.tech_count}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {t.site_count}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {t.machine_count}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground tabular-nums">
                    {dateFmt.format(new Date(t.created_at))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
